<div class="filter-list-cell">
  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoadingData">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading data...</p>
  </div>

  <!-- Data Table -->
  <div class="table-container" *ngIf="!isLoadingData && columns.length > 0">

    <!-- Compact Filter Bar -->
    <div class="filter-bar">
      <div class="filter-bar-left">
        <button mat-stroked-button (click)="openFilterDialog()" class="filter-btn" *ngIf="filterDisplayMode !== 'inline'">
          <mat-icon>filter_alt</mat-icon>
          Filters
          <span *ngIf="hasActiveFilters" class="filter-badge">{{ filterRows.length }}</span>
        </button>
        <span *ngIf="hasActiveFilters && filterDisplayMode !== 'inline'" class="active-filters-text">
          {{ filterRows.length }} filter(s) active
        </span>
        <button mat-icon-button
                *ngIf="hasActiveFilters && filterDisplayMode !== 'inline'"
                (click)="clearFilters()"
                matTooltip="Clear all filters"
                class="clear-btn">
          <mat-icon>clear</mat-icon>
        </button>
      </div>

      <div class="filter-bar-right">
        <button mat-icon-button
                (click)="refreshTableData()"
                matTooltip="Refresh data"
                class="refresh-btn"
                [disabled]="isLoadingData">
          <mat-icon>refresh</mat-icon>
        </button>
        <button mat-raised-button
                *ngIf="allowCreate"
                color="primary"
                (click)="onCreateClick()"
                class="create-btn">
          <mat-icon>add</mat-icon>
          {{ createButtonLabel }}
        </button>
      </div>
    </div>

    <!-- Inline Filters (shown above table when filterDisplayMode is 'inline') -->
    <div class="inline-filters" *ngIf="filterDisplayMode === 'inline'">
      <div class="inline-filter-chips">
        <div class="inline-filter-chip" *ngFor="let filterRow of filterRows; let i = index">

          <mat-form-field appearance="outline" class="inline-filter-field" subscriptSizing="dynamic">
            <mat-select [(ngModel)]="filterRow.fieldName"
                        (selectionChange)="onInlineFilterFieldChange(filterRow)"
                        placeholder="Field">
              <mat-option *ngFor="let col of getAvailableFilterColumns()" [value]="col.fieldName">
                {{ col.displayName }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <button *ngIf="filterRow.fieldName && filterRow.availableMatchModes?.length > 1"
                  class="inline-match-mode-btn"
                  [matMenuTriggerFor]="matchModeMenu"
                  [matTooltip]="getMatchModeLabel(filterRow.matchMode)">
            {{ getMatchModeAbbrev(filterRow.matchMode) }}
          </button>
          <mat-menu #matchModeMenu="matMenu" class="inline-match-mode-menu">
            <button mat-menu-item *ngFor="let mode of filterRow.availableMatchModes"
                    (click)="onInlineMatchModeChange(filterRow, mode.value)">
              <span [class.active-mode]="filterRow.matchMode === mode.value">{{ mode.label }}</span>
            </button>
          </mat-menu>

          <ng-container *ngIf="isDateTimeFilterRow(filterRow); else inlineTextInput">
            <mat-form-field appearance="outline" class="inline-filter-value" subscriptSizing="dynamic">
              <input matInput
                     [matDatepicker]="inlinePicker"
                     [(ngModel)]="filterRow.dateValue"
                     (dateChange)="onInlineFilterDateChange(filterRow)"
                     placeholder="Date" />
              <mat-datepicker-toggle matSuffix [for]="inlinePicker"></mat-datepicker-toggle>
              <mat-datepicker #inlinePicker></mat-datepicker>
            </mat-form-field>
          </ng-container>
          <ng-template #inlineTextInput>
            <mat-form-field appearance="outline" class="inline-filter-value" subscriptSizing="dynamic"
                            *ngIf="filterRow.fieldName">
              <input matInput
                     [(ngModel)]="filterRow.value"
                     (ngModelChange)="onInlineFilterValueChange()"
                     [placeholder]="filterRow.matchMode === 'between' ? 'Value' : 'Value (comma-separate for multiple)'"
                     [matTooltip]="filterRow.matchMode === 'between' ? '' : 'Separate multiple values with commas'" />
            </mat-form-field>
          </ng-template>

          <button mat-icon-button (click)="removeInlineFilter(i)"
                  class="inline-remove-btn" matTooltip="Remove filter">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <button mat-stroked-button (click)="addInlineFilter()" class="inline-add-btn">
          <mat-icon>add</mat-icon>
          Add
        </button>
        <button mat-button (click)="clearInlineFilters()" *ngIf="hasActiveFilters" class="inline-clear-btn">
          Clear
        </button>
      </div>
    </div>

    <!-- Custom table instead of Angular Material table for better dynamic column support -->
    <div class="custom-table">
      <div class="table-header" #tableHeader>
        <div class="header-row">
          <!-- Selection Header (only show when row selection is enabled) -->
          <div class="header-cell selection-header" *ngIf="rowSelectionEnabled">
            <div class="header-wrapper">
              <div class="header-content">
                <span class="header-text">Select</span>
              </div>
            </div>
          </div>

          <div class="header-cell sortable" *ngFor="let column of getDisplayedColumns(); let first = first; trackBy: trackByColumn"
               [class.first-column-bold]="first && cell.configuration.firstColumnBold"
               (click)="onSortColumn(column)">
            <div class="header-wrapper">
              <div class="header-content">
                <span class="header-text">{{ column.displayName }}</span>
                <mat-icon class="sort-icon" *ngIf="sortColumn === column.fieldName">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </mat-icon>
              </div>
            </div>
          </div>

          <!-- Navigation Header (only show when navigation is enabled) -->
          <div class="header-cell navigation-header" *ngIf="navigationEnabled">
            <div class="header-wrapper">
              <div class="header-content">
                <span class="header-text">Actions</span>
              </div>
            </div>
          </div>

          <!-- Delete Header (only show when delete is enabled and not in edit mode) -->
          <div class="header-cell delete-header" *ngIf="deleteEnabled && !isEditMode">
            <div class="header-wrapper">
              <div class="header-content">
                <span class="header-text"></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-body" #tableBody>
        <div class="body-row"
             *ngFor="let row of filteredData; let rowIndex = index"
             [class.selected]="isRowSelected(rowIndex)"
             [class.navigation-highlighted]="isRowNavigationHighlighted(rowIndex)"
             [class.navigatable]="isRowNavigatable()"
             [style.backgroundColor]="getRowBackgroundColor(rowIndex)"
             (click)="onRowClick(row, rowIndex)">

          <!-- Selection Cell (only show when row selection is enabled) -->
          <div class="selection-cell" *ngIf="rowSelectionEnabled">
            <mat-checkbox
              [checked]="isRowSelected(rowIndex)"
              (change)="onCheckboxChange(row, rowIndex, $event.checked)"
              (click)="$event.stopPropagation()">
            </mat-checkbox>
          </div>

          <div class="body-cell"
               *ngFor="let column of getDisplayedColumns(); let first = first; trackBy: trackByColumn"
               [class.first-column-bold]="first && cell.configuration.firstColumnBold"
               [title]="row[column.fieldName]">
            <span [ngSwitch]="column.dataType">
              <span *ngSwitchCase="'date'">{{ formatDate(row[column.fieldName]) }}</span>
              <span *ngSwitchCase="'number'">{{ formatNumber(row[column.fieldName]) }}</span>
              <span *ngSwitchDefault>{{ row[column.fieldName] }}</span>
            </span>
          </div>

          <!-- Navigation Icon (only show when navigation is enabled) -->
          <div class="navigation-cell" *ngIf="navigationEnabled">
            <button mat-icon-button
                    class="nav-btn"
                    (click)="onRowClick(row, rowIndex); $event.stopPropagation()"
                    matTooltip="Navigate to details">
              <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>

          <!-- Delete Icon (only show when delete is enabled and not in edit mode) -->
          <div class="delete-cell" *ngIf="deleteEnabled && !isEditMode">
            <button mat-icon-button
                    class="delete-btn"
                    (click)="onDeleteRow(row, rowIndex, $event)"
                    matTooltip="Delete row">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination / Row Count -->
    <div class="table-pagination" *ngIf="totalRecords > 0">
      <ng-container *ngIf="paginationEnabled">
        <button mat-icon-button [disabled]="currentPage === 0" (click)="onPageChange(0)" matTooltip="First page">
          <mat-icon>first_page</mat-icon>
        </button>
        <button mat-icon-button [disabled]="currentPage === 0" (click)="onPageChange(currentPage - 1)" matTooltip="Previous page">
          <mat-icon>chevron_left</mat-icon>
        </button>
      </ng-container>
      <span class="page-info" *ngIf="paginationEnabled">Page {{ currentPage + 1 }} of {{ totalPages }} ({{ totalRecords }} records)</span>
      <span class="page-info" *ngIf="!paginationEnabled">{{ totalRecords }} records</span>
      <ng-container *ngIf="paginationEnabled">
        <button mat-icon-button [disabled]="currentPage >= totalPages - 1" (click)="onPageChange(currentPage + 1)" matTooltip="Next page">
          <mat-icon>chevron_right</mat-icon>
        </button>
        <button mat-icon-button [disabled]="currentPage >= totalPages - 1" (click)="onPageChange(totalPages - 1)" matTooltip="Last page">
          <mat-icon>last_page</mat-icon>
        </button>
      </ng-container>
    </div>

  </div>

  <!-- No Dataset Selected -->
  <div class="no-dataset" *ngIf="!isEditMode && !cell.dataBinding.datasetId">
    <mat-icon>dataset</mat-icon>
    <p>No dataset selected</p>
    <small>Configure this cell to select a dataset</small>
  </div>
</div>

<!-- Filter Dialog Template -->
<ng-template #filterDialog>
  <h2 mat-dialog-title>Filters</h2>
  <mat-dialog-content>
    <div class="filter-dialog-content">
      <!-- Filter rows -->
      <div class="filter-rows">
        <div class="filter-row" *ngFor="let filterRow of filterRows; let i = index">
          <mat-form-field appearance="outline" class="filter-column-field">
            <mat-label>Column</mat-label>
            <mat-select [(ngModel)]="filterRow.fieldName" (selectionChange)="onFilterFieldChange(filterRow)">
              <mat-option *ngFor="let col of getAvailableFilterColumns()" [value]="col.fieldName">
                {{ col.displayName }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-match-mode-field" *ngIf="filterRow.fieldName && filterRow.availableMatchModes">
            <mat-label>Match Mode</mat-label>
            <mat-select [(ngModel)]="filterRow.matchMode" (selectionChange)="onMatchModeChange(filterRow)">
              <mat-option *ngFor="let mode of filterRow.availableMatchModes" [value]="mode.value">
                {{ mode.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <!-- DateTime filter value -->
          <ng-container *ngIf="isDateTimeFilterRow(filterRow)">
            <mat-form-field appearance="outline" class="filter-value-field">
              <mat-label>{{ filterRow.matchMode === 'between' ? 'From Date' : 'Date' }}</mat-label>
              <input matInput
                     [matDatepicker]="filterPicker1"
                     [(ngModel)]="filterRow.dateValue"
                     (dateChange)="onFilterDateChange(filterRow, false)"
                     placeholder="MM/DD/YYYY" />
              <mat-datepicker-toggle matSuffix [for]="filterPicker1"></mat-datepicker-toggle>
              <mat-datepicker #filterPicker1></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-value-field" *ngIf="filterRow.matchMode === 'between'">
              <mat-label>To Date</mat-label>
              <input matInput
                     [matDatepicker]="filterPicker2"
                     [(ngModel)]="filterRow.dateValue2"
                     (dateChange)="onFilterDateChange(filterRow, true)"
                     placeholder="MM/DD/YYYY" />
              <mat-datepicker-toggle matSuffix [for]="filterPicker2"></mat-datepicker-toggle>
              <mat-datepicker #filterPicker2></mat-datepicker>
            </mat-form-field>
          </ng-container>

          <!-- Non-DateTime filter value -->
          <ng-container *ngIf="!isDateTimeFilterRow(filterRow)">
            <mat-form-field appearance="outline" class="filter-value-field">
              <mat-label>{{ filterRow.matchMode === 'between' ? 'From Value' : 'Value' }}</mat-label>
              <input matInput [(ngModel)]="filterRow.value"
                     [placeholder]="filterRow.matchMode === 'between' ? 'Enter value' : 'Enter value (comma-separate for multiple)'" />
              <mat-hint *ngIf="filterRow.matchMode !== 'between'">Separate multiple values with commas</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="filter-value-field" *ngIf="filterRow.matchMode === 'between'">
              <mat-label>To Value</mat-label>
              <input matInput [(ngModel)]="filterRow.value2" placeholder="Enter end value" />
            </mat-form-field>
          </ng-container>

          <button mat-icon-button (click)="removeFilterRow(i)" color="warn" class="remove-filter-btn">
            <mat-icon>delete</mat-icon>
          </button>
        </div>

        <!-- Empty state -->
        <div class="no-filters" *ngIf="filterRows.length === 0">
          <mat-icon>filter_alt_off</mat-icon>
          <p>No filters added</p>
          <small>Click "Add Filter" to create a filter</small>
        </div>
      </div>

      <button mat-stroked-button (click)="addFilterRow()" class="add-filter-btn">
        <mat-icon>add</mat-icon>
        Add Filter
      </button>
    </div>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Cancel</button>
    <button mat-button (click)="clearFilters()" [disabled]="filterRows.length === 0">Clear All</button>
    <button mat-raised-button color="primary" (click)="applyFilters()" mat-dialog-close>Apply Filters</button>
  </mat-dialog-actions>
</ng-template>

<!-- Delete Confirmation Dialog Template -->
<ng-template #deleteConfirmDialog>
  <h2 mat-dialog-title>Confirm Delete</h2>
  <mat-dialog-content>
    <p>Are you sure you want to delete this entry? This action cannot be undone.</p>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button [mat-dialog-close]="false">Cancel</button>
    <button mat-raised-button color="warn" [mat-dialog-close]="true">Delete</button>
  </mat-dialog-actions>
</ng-template>
